import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Globe,
  LayoutGrid,
  RotateCcw,
  Search as SearchIcon,
  ShoppingCart,
  Sliders,
  Users,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { EmptyState } from '@/components/ui/EmptyState';
import { MetricTile } from '@/components/marketing/MetricTile';
import { TrendChart } from '@/components/marketing/TrendChart';
import { comparisonLabel } from '@/components/marketing/RangeSelector';
import { CHART_SERIES } from '@/lib/brand';
import { formatCurrency } from '@/lib/formatters';
import {
  useAdsAnalytics,
  useAmazonAnalytics,
  useSearchConsole,
  useSocialAnalytics,
  useWebsiteTotals,
} from '@/features/marketing/hooks';
import {
  buildMarketingTiles,
  DEFAULT_MARKETING_TILE_IDS,
  indexTiles,
  MARKETING_TILE_GROUPS,
  type MarketingTile,
} from '@/features/marketing/tiles';
import { useMarketingDashboardLayout } from '@/features/marketing/preferences';
import {
  formatCompactNumber,
  formatRatioPercent,
  isoDaysAgo,
  MARKETING_RANGE_DAYS,
  type MarketingRange,
} from '@/features/marketing/range';

interface OverviewTabProps {
  range: MarketingRange;
}

/**
 * The dashboard's landing tab: a user-customizable grid of headline metrics
 * pulled from every marketing channel, plus a cross-channel trend and a
 * channel scorecard.
 *
 * Every operator picks their own tile set (persisted per-user in
 * `user_preferences`), because the people in the bi-weekly review care about
 * different things — paid efficiency for one, organic growth for another. The
 * tile catalog lives in `features/marketing/tiles.ts`; this component only
 * arranges what the catalog produces.
 */
export function OverviewTab({ range }: OverviewTabProps) {
  const website = useWebsiteTotals(range);
  const search = useSearchConsole(range);
  const social = useSocialAnalytics(range);
  const ads = useAdsAnalytics(range);
  const amazon = useAmazonAnalytics(range);

  const loading =
    website.isLoading ||
    search.isLoading ||
    social.isLoading ||
    ads.isLoading ||
    amazon.isLoading;

  const tiles = useMemo(
    () =>
      buildMarketingTiles({
        website: website.data,
        search: search.data,
        social: social.data,
        ads: ads.data,
        amazon: amazon.data,
      }),
    [website.data, search.data, social.data, ads.data, amazon.data],
  );

  const tileIndex = useMemo(() => indexTiles(tiles), [tiles]);
  const allTileIds = useMemo(() => tiles.map((t) => t.id), [tiles]);

  const layout = useMarketingDashboardLayout(allTileIds, DEFAULT_MARKETING_TILE_IDS);

  // The catalog is only fully populated once the queries resolve, so during
  // the initial load fall back to the default ids — otherwise the saved
  // layout would be filtered against an empty catalog and render nothing.
  const pinnedIds = allTileIds.length > 0 ? layout.tiles : DEFAULT_MARKETING_TILE_IDS;
  const pinned = pinnedIds
    .map((id) => tileIndex.get(id))
    .filter((tile): tile is MarketingTile => !!tile);

  const deltaLabel = comparisonLabel(range);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-medium tracking-headline text-foreground">
            Headline metrics
          </h2>
          <p className="text-sm text-muted-foreground">
            {pinned.length} of {tiles.length} metrics pinned
            {layout.isCustomized ? ' · customized for you' : ' · using the default set'}.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {layout.isSaving && (
            <span className="text-xs text-muted-foreground">Saving…</span>
          )}
          {layout.isCustomized && (
            <Button variant="ghost" size="sm" onClick={layout.resetToDefault}>
              <RotateCcw className="h-4 w-4" />
              Reset
            </Button>
          )}
          <TilePicker
            tiles={tiles}
            pinnedIds={pinnedIds}
            onToggle={layout.toggleTile}
            disabled={allTileIds.length === 0}
          />
        </div>
      </div>

      {layout.saveError && (
        <p className="text-xs text-fresh-salmon-dark">
          Couldn't save your layout: {layout.saveError}
        </p>
      )}

      {pinned.length === 0 ? (
        <EmptyState
          icon={LayoutGrid}
          title="No metrics pinned"
          action={
            <Button variant="outline" onClick={layout.resetToDefault}>
              Use the default set
            </Button>
          }
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {pinned.map((tile) => (
            <MetricTile
              key={tile.id}
              tile={tile}
              loading={loading}
              deltaLabel={deltaLabel}
            />
          ))}
        </div>
      )}

      <BlendedTrendCard range={range} />

      <ChannelScorecard
        website={website.data}
        search={search.data}
        social={social.data}
        ads={ads.data}
        amazon={amazon.data}
      />
    </div>
  );
}

// ─── Tile picker ───────────────────────────────────────────────────────

interface TilePickerProps {
  tiles: MarketingTile[];
  pinnedIds: readonly string[];
  onToggle: (id: string) => void;
  disabled?: boolean;
}

function TilePicker({ tiles, pinnedIds, onToggle, disabled }: TilePickerProps) {
  const pinnedSet = new Set(pinnedIds);
  const byGroup = MARKETING_TILE_GROUPS.map((group) => ({
    group,
    items: tiles.filter((t) => t.group === group),
  })).filter((entry) => entry.items.length > 0);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" disabled={disabled}>
          <Sliders className="h-4 w-4" />
          Customize
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="max-h-[70vh] w-72 overflow-y-auto">
        <DropdownMenuLabel>Pin the metrics you review</DropdownMenuLabel>
        {byGroup.map((entry, index) => (
          <div key={entry.group}>
            {index > 0 && <DropdownMenuSeparator />}
            <DropdownMenuLabel className="text-[11px] uppercase tracking-wide">
              {entry.group}
            </DropdownMenuLabel>
            {entry.items.map((tile) => (
              <DropdownMenuCheckboxItem
                key={tile.id}
                checked={pinnedSet.has(tile.id)}
                // Radix closes the menu on select by default; pinning several
                // metrics in a row is the common case, so keep it open.
                onSelect={(event) => event.preventDefault()}
                onCheckedChange={() => onToggle(tile.id)}
              >
                <span className="flex-1">{tile.label}</span>
                {!tile.available && (
                  <span className="ml-2 text-[10px] text-muted-foreground">not connected</span>
                )}
              </DropdownMenuCheckboxItem>
            ))}
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ─── Blended trend ─────────────────────────────────────────────────────

/**
 * Puts the three demand signals on one timeline — site visitors, organic
 * search clicks, and paid spend — so the team can see whether traffic moved
 * because of spend or independently of it.
 *
 * Spend sits on the right-hand axis because dollars and visitor counts differ
 * by orders of magnitude; sharing an axis would flatten whichever series is
 * smaller into the baseline.
 */
function BlendedTrendCard({ range }: { range: MarketingRange }) {
  const website = useWebsiteTotals(range);
  const search = useSearchConsole(range);
  const ads = useAdsAnalytics(range);

  const days = MARKETING_RANGE_DAYS[range];

  const data = useMemo(() => {
    const byDay = new Map<
      string,
      { day: string; users: number; searchClicks: number; spend: number }
    >();
    const ensure = (day: string) => {
      const existing = byDay.get(day);
      if (existing) return existing;
      const created = { day, users: 0, searchClicks: 0, spend: 0 };
      byDay.set(day, created);
      return created;
    };

    // Seed every day in the window so a channel with a reporting lag (Search
    // Console runs 2-3 days behind) doesn't shorten the whole chart.
    for (let i = days - 1; i >= 0; i--) ensure(isoDaysAgo(i));

    for (const point of website.data?.daily ?? []) ensure(point.day).users = point.users;
    for (const point of search.data?.daily ?? []) {
      ensure(point.day).searchClicks = point.clicks;
    }
    for (const point of ads.data?.daily ?? []) {
      ensure(point.day).spend = point.spendCents / 100;
    }

    return Array.from(byDay.values()).sort((a, b) => a.day.localeCompare(b.day));
  }, [website.data, search.data, ads.data, days]);

  const anyData = website.data?.connected || search.data?.connected || ads.data?.connected;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Demand across channels</CardTitle>
      </CardHeader>
      <CardContent>
        {anyData ? (
          <TrendChart
            data={data}
            height={300}
            formatRightAxis={(value) => `$${Math.round(value).toLocaleString()}`}
            series={[
              {
                key: 'spend',
                label: 'Ad spend',
                color: CHART_SERIES[4],
                kind: 'bar',
                axis: 'right',
                format: (value) => formatCurrency(Math.round(value * 100)),
              },
              { key: 'users', label: 'Website users', color: CHART_SERIES[0], kind: 'area' },
              {
                key: 'searchClicks',
                label: 'Search clicks',
                color: CHART_SERIES[3],
                kind: 'line',
              },
            ]}
          />
        ) : (
          <p className="py-10 text-center text-sm text-muted-foreground">
            No demand data for this window.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Channel scorecard ─────────────────────────────────────────────────

interface ChannelScorecardProps {
  website: ReturnType<typeof useWebsiteTotals>['data'];
  search: ReturnType<typeof useSearchConsole>['data'];
  social: ReturnType<typeof useSocialAnalytics>['data'];
  ads: ReturnType<typeof useAdsAnalytics>['data'];
  amazon: ReturnType<typeof useAmazonAnalytics>['data'];
}

/**
 * One-line health summary per channel with a link into its own tab, so the
 * review can start broad and drill in rather than paging through every tab.
 */
function ChannelScorecard({
  website,
  search,
  social,
  ads,
  amazon,
}: ChannelScorecardProps) {
  const channels: Array<{
    label: string;
    to: string;
    icon: typeof Globe;
    connected: boolean;
    headline: string;
    detail: string;
  }> = [
    {
      label: 'Website',
      to: '/marketing/dashboard?tab=website',
      icon: Globe,
      connected: !!website?.connected,
      headline: `${formatCompactNumber(website?.totals.users ?? 0)} users`,
      detail: website
        ? `${formatCompactNumber(website.totals.sessions)} sessions · ${formatRatioPercent(
            website.totals.engagementRate,
          )} engaged`
        : 'Google Analytics 4 not connected',
    },
    {
      label: 'Search',
      to: '/marketing/dashboard?tab=search',
      icon: SearchIcon,
      connected: !!search?.connected,
      headline: `${formatCompactNumber(search?.totals.clicks ?? 0)} clicks`,
      detail: search?.connected
        ? `${formatCompactNumber(search.totals.impressions)} impressions · position ${search.totals.position.toFixed(1)}`
        : 'Search Console not connected',
    },
    {
      label: 'Social',
      to: '/marketing/dashboard?tab=social',
      icon: Users,
      connected: !!social?.connected,
      headline: `${formatCompactNumber(social?.totals.followers ?? 0)} followers`,
      detail: social?.connected
        ? `${social.totals.followerGrowth >= 0 ? '+' : ''}${social.totals.followerGrowth.toLocaleString()} this period · ${formatCompactNumber(social.totals.engagements)} engagements`
        : 'No social accounts connected',
    },
    {
      label: 'Paid ads',
      to: '/marketing/advertising',
      icon: LayoutGrid,
      connected: !!ads?.connected,
      headline: formatCurrency(ads?.totals.spendCents ?? 0, { whole: true }),
      detail: ads?.connected
        ? `${ads.totals.roas.toFixed(2)}× ROAS · ${formatCompactNumber(ads.totals.conversions)} conversions`
        : 'No ad platforms connected',
    },
    {
      label: 'Amazon',
      to: '/marketing/dashboard?tab=amazon',
      icon: ShoppingCart,
      connected: !!amazon?.connected,
      headline: formatCurrency(amazon?.totals.revenueCents ?? 0, { whole: true }),
      detail: amazon?.connected
        ? `${amazon.totals.orders.toLocaleString()} orders · ${formatCurrency(amazon.totals.aovCents)} AOV`
        : 'Amazon Seller Central not connected',
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Channel scorecard</CardTitle>
      </CardHeader>
      <CardContent className="divide-y divide-border">
        {channels.map((channel) => {
          const Icon = channel.icon;
          return (
            <Link
              key={channel.label}
              to={channel.to}
              className="flex items-center gap-4 py-3 transition-colors first:pt-0 last:pb-0 hover:bg-secondary/40"
            >
              <Icon className="h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={1.8} />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-foreground">{channel.label}</p>
                <p className="truncate text-xs text-muted-foreground">{channel.detail}</p>
              </div>
              {channel.connected ? (
                <p className="shrink-0 tabular-nums text-sm font-medium text-foreground">
                  {channel.headline}
                </p>
              ) : (
                <Badge variant="muted" className="shrink-0">
                  Not connected
                </Badge>
              )}
            </Link>
          );
        })}
      </CardContent>
    </Card>
  );
}
